import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import CTA from "../components/cta";
import "../components/faq.css";
import "./villas-for-sale-abu-dhabi.css";
import "../components/SEOManager";

const investmentAdvantages = [
  { icon: "fas fa-chart-line", title: "Consistent rental yields and capital appreciation" },
  { icon: "fas fa-file-invoice-dollar", title: "Tax-free ownership benefits" },
  { icon: "fas fa-key", title: "High demand for rental apartments" },
  { icon: "fas fa-city", title: "World-class infrastructure and lifestyle amenities" },
];

const apartmentOptions = [
  { icon: "fas fa-door-closed", title: "Studio Apartments", text: "Affordable, compact, and ideal for investors targeting rental income." },
  { icon: "fas fa-people-roof", title: "1\u20133 Bedroom Apartments", text: "Perfect for professionals and families looking for comfort and convenience." },
  { icon: "fas fa-gem", title: "Luxury Apartments", text: "High-end residences with premium finishes, smart features, and breath-taking views." },
  { icon: "fas fa-file-signature", title: "Off-Plan Apartments", text: "Great for investors seeking flexible payment plans and future value growth." },
];

const faqs = [
  {
    icon: "fas fa-chart-line",
    question: "Are Apartments a good investment in Abu Dhabi?",
    answer: "Yes. They are. You can buy the right apartment to buy in Abu Dhabi and enjoy a great return.",
  },
  {
    icon: "fas fa-globe",
    question: "Can foreigners buy apartments in Abu Dhabi?",
    answer: "If you are a foreign national, you can buy an apartment for yourself in certain areas allowed for you. In those areas, you get full ownership rights.",
  },
  {
    icon: "fas fa-handshake",
    question: "Why should I choose NV Real Estate?",
    answer: "NV Real Estate offers expert guidance, transparent dealings, and personalized support to help you find and buy the best apartment with confidence.",
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map(({ question, answer }) => ({
    "@type": "Question",
    name: question,
    acceptedAnswer: { "@type": "Answer", text: answer },
  })),
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://nvrealestate.ae/" },
    { "@type": "ListItem", position: 2, name: "Apartments to Buy in Abu Dhabi", item: "https://nvrealestate.ae/apartments-to-buy-abu-dhabi" },
  ],
};

function ApartmentsToBuyInAbuDhabi() {
  const [activeFaq, setActiveFaq] = useState(0);

  return (
    <>
      <Helmet>
        <title>Apartments to Buy in Abu Dhabi – Apartments for Sale</title>
        <meta
          name="description"
          content="Looking for a well-located & built apartment with great lifestyle assurance? NV Real Estate has so many apartments to buy in Abu Dhabi. Talk now to find the best one for you."
        />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <main
        className="villa-page"
        style={{ "--villa-section-image": `url(${process.env.PUBLIC_URL}/assets/images/abu.jpg)` }}
      >
        <section className="villa-hero" aria-labelledby="villa-page-title">
          <div
            className="villa-hero__image"
            aria-hidden="true"
            style={{ backgroundImage: `url(/images/seo/apartments-to-buy-in-Abu-Dhabi/01.png)` }}
          />
          <div className="villa-hero__overlay" aria-hidden="true" />
          <div className="container villa-hero__inner">
            <div className="villa-hero__content">
              <span className="villa-eyebrow">Abu Dhabi apartment specialists</span>
              <h1 id="villa-page-title">Buy Your Apartment in Abu Dhabi with Us</h1>
              <div className="villa-hero__actions">
                <Link className="villa-button villa-button--primary" to="/property-search-result?offering_type=Sale&city=&developer=&developer_name=&project=&project_name=&property_type=Apartment&bedrooms=&bathrooms=&minArea=&maxArea=&minPrice=&maxPrice=">
                  Explore apartments
                </Link>
                <a className="villa-button villa-button--ghost" href="tel:+971501577822">
                  Call +971 50 157 7822
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="villa-intro villa-section">
          <div className="container villa-intro__grid">
            <div className="villa-intro__image">
              <img
                src="https://imagedelivery.net/WefzwPpyYAgz4nGrybaw0Q/dbdb0598-6353-4aee-9707-f36e4e3f0900/w=550,sharpen=1"
                alt="Apartments to buy in Abu Dhabi"
              />
              <div className="villa-intro__note">
                <strong>Off-market access</strong>
                <span>Including properties not publicly listed</span>
              </div>
            </div>
            <div className="villa-section-heading villa-intro__content">
              <span className="villa-kicker">Nova Vision Real Estate</span>
              <h2>Buy Your Apartment in Abu Dhabi with Us</h2>
              <p>
                Nova Vision Real Estate is a trusted partner for finding premium apartments in Abu
                Dhabi. We work closely with leading and reputable developers, which enables us to
                give you access to carefully selected properties that match your exact requirements
                and goals.
              </p>
              <p>
                Regardless of whether you are seeking an apartment to call home and live with your
                family to make significant returns, we have the best solutions for your needs. For
                those seeking a home, we look for communities that provide an excellent quality of
                life, offer modern conveniences, and offer long-term value. For those seeking an
                investment, we look for locations with strong rental potential.
              </p>
              <p>
                The most exciting fact about our real estate solutions is that we bring you the
                opportunity to access exclusive and off-market properties that are not publicly
                listed. This enables you to explore opportunities that most people do not know
                about. With our expertise, you are assured of making a secure property investment.
              </p>
            </div>
          </div>
        </section>

        <section className="villa-benefits villa-section" aria-labelledby="why-buy-heading">
          <div className="container">
            <div className="villa-section-heading villa-section-heading--center">
              <span className="villa-kicker">Why buy here</span>
              <h2 id="why-buy-heading">Why Buy an Apartment for Yourself in Abu Dhabi?</h2>
              <p>
                Abu Dhabi is really a great place to buy an apartment in Abu Dhabi for its economic
                stability and high-quality developments. If you buy an apartment for yourself here,
                you not only buy a property for yourself, but you buy a future-ready asset with many
                advantages.
              </p>
              <p><strong>Key Investment Advantages:</strong></p>
            </div>
            <div className="villa-benefits__grid">
              {investmentAdvantages.map((item, index) => (
                <article className="villa-benefit-card" key={item.title}>
                  <span className="villa-benefit-card__number" aria-hidden="true">0{index + 1}</span>
                  <span className="villa-benefit-card__icon" aria-hidden="true"><i className={item.icon} /></span>
                  <h3>{item.title}</h3>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="villa-benefits villa-section" aria-labelledby="options-heading">
          <div className="container">
            <div className="villa-section-heading villa-section-heading--center">
              <span className="villa-kicker">Apartment options</span>
              <h2 id="options-heading">Explore the Best Apartment Options</h2>
              <p>
                NV Real Estate has access to the best apartments developed by trusted developers in
                Abu Dhabi. Hence, you can feel confident about getting the property of your choice
                with us.
              </p>
            </div>
            <div className="villa-benefits__grid">
              {apartmentOptions.map((item, index) => (
                <article className="villa-benefit-card" key={item.title}>
                  <span className="villa-benefit-card__number" aria-hidden="true">0{index + 1}</span>
                  <span className="villa-benefit-card__icon" aria-hidden="true"><i className={item.icon} /></span>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </article>
              ))}
            </div>
            <p className="villa-process__closing">
              Our advanced property finder Abu Dhabi platform allows you to filter, compare, and
              evaluate these options efficiently, saving time while ensuring better decisions.
            </p>
          </div>
        </section>

        <section className="villa-advisor villa-section">
          <div className="container">
            <div className="villa-advisor__card">
              <div>
                <span className="villa-kicker">Why Nova Vision</span>
                <h2>Why Trust Nova Vision Real Estate?</h2>
                <p>
                  Choosing the right real estate partner can make a significant difference in your
                  buying experience.
                </p>
                <a className="villa-button villa-button--light" href="https://wa.me/971501577822" target="_blank" rel="noreferrer">
                  Chat on WhatsApp
                </a>
                <p className="villa-advisor__closing">
                  We have the best apartment for you in Abu Dhabi. Call us now to buy the right one.
                </p>
              </div>
              <ul>
                <li><strong>Proven Market Expertise</strong> &mdash; Our team has in-depth knowledge of Abu Dhabi&rsquo;s real estate trends and investment opportunities.</li>
                <li><strong>Transparency and Trust</strong> &mdash; We provide accurate property information with no hidden details.</li>
                <li><strong>End-to-End Support</strong> &mdash; From property selection to final documentation, we guide you through every step.</li>
                <li><strong>Client-Centric Approach</strong> &mdash; We focus on your requirements, preferences, lifestyle, and goals.</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="nv-faq-section villa-faq" aria-labelledby="villa-faq-title">
          <div className="nv-faq-container">
            <header className="nv-faq-heading">
              <span className="nv-faq-label">Buyer questions</span>
              <h2 id="villa-faq-title">Frequently Asked Questions</h2>
              <p>Practical answers to help you begin your apartment search with confidence.</p>
            </header>

            <div className="villa-faq__main">
              <div className="nv-faq-accordion">
                {faqs.map((faq, index) => {
                  const isActive = activeFaq === index;
                  return (
                    <article className={`nv-faq-item${isActive ? " active" : ""}`} key={faq.question}>
                      <button
                        className="nv-faq-question"
                        type="button"
                        onClick={() => setActiveFaq((current) => (current === index ? null : index))}
                        aria-expanded={isActive}
                        aria-controls={`apt-buy-faq-answer-${index}`}
                      >
                        <span className="nv-faq-question-icon"><i className={faq.icon} /></span>
                        <strong>{faq.question}</strong>
                        <span className="nv-faq-toggle" aria-hidden="true" />
                      </button>
                      <div
                        className="nv-faq-answer"
                        id={`apt-buy-faq-answer-${index}`}
                        aria-hidden={!isActive}
                      >
                        <p>{faq.answer}</p>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>

            <div className="nv-faq-trust-strip">
              <div className="nv-faq-trust-item"><i className="fas fa-home" /><div><strong>Curated Apartments</strong><span>Matched to Your Brief</span></div></div>
              <div className="nv-faq-trust-item"><i className="fas fa-map-marker-alt" /><div><strong>Local Knowledge</strong><span>Across Abu Dhabi</span></div></div>
              <div className="nv-faq-trust-item"><i className="fas fa-file-signature" /><div><strong>Purchase Support</strong><span>Offer to Transfer</span></div></div>
              <div className="nv-faq-trust-item"><i className="fas fa-headset" /><div><strong>Dedicated Advisor</strong><span>One Point of Contact</span></div></div>
            </div>
            <p className="villa-faq__contact">
              Call us now at <a href="tel:+971501577822">+971-501577822</a>. You can also email us
              your requirements at <a href="mailto:info@nvrealestate.ae">info@nvrealestate.ae</a>.
            </p>
          </div>
        </section>
      </main>

      <CTA />
    </>
  );
}

export default ApartmentsToBuyInAbuDhabi;